import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { api } from '../lib/api';
import { US_STATES } from '../lib/shop';
import { money } from '../hooks/usePageContent';
import RentalDatePicker, { useAvailability, rentalEnd, overlaps, parseIso } from './RentalDatePicker';

const EMPTY = { name: '', email: '', phone: '', company: '', start_date: '', duration_type: 'daily', duration_count: 1, delivery: 'pickup', city: '', state: 'NC', notes: '' };
const inputCls = 'mt-1 w-full border border-neutral-300 bg-white px-3 py-2.5 text-neutral-900 focus:outline-none focus:border-brand-primary';

const L = ({ label, children, span = '' }) => (
    <label className={`block ${span}`}><span className="label-caps text-xs text-neutral-500">{label}</span>{children}</label>
);

const Estimate = ({ machine, f }) => {
    const rate = machine && (f.duration_type === 'weekly' ? machine.rental_weekly : machine.rental_daily);
    const deposit = Number(machine?.rental_deposit) || 0;
    return (
        <div className="border border-neutral-200 bg-white p-5 space-y-2 text-sm" data-testid="rental-estimate">
            {machine ? (
                <div className="flex justify-between gap-3"><span>{machine.name} · {f.duration_count} {f.duration_type === 'weekly' ? 'wk' : 'day'}</span>
                    <span data-testid="rental-estimate-rental">{rate ? money(rate * f.duration_count) : 'Rate on request'}</span></div>
            ) : <div className="text-neutral-500">Pick a machine to see your estimate.</div>}
            <div className="border-t border-neutral-200 pt-2 flex justify-between font-bold text-neutral-900">
                <span>Due today (refundable deposit)</span><span data-testid="rental-estimate-deposit">{deposit > 0 ? money(deposit) : 'Nothing'}</span>
            </div>
            <p className="text-xs text-neutral-500">The remaining balance is invoiced after we confirm your dates. Dry ice, compressor and delivery are quoted separately.</p>
        </div>
    );
};

const RentalForm = ({ fleet, machineId, setMachineId }) => {
    const [f, setF] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(null);
    const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
    const machine = useMemo(() => fleet.find((m) => m.id === machineId), [fleet, machineId]);
    const booked = useAvailability(machineId);
    const endDate = f.start_date ? rentalEnd(f.start_date, f.duration_type, Number(f.duration_count) || 1) : '';
    const clash = !!f.start_date && overlaps(booked, f.start_date, endDate);
    useEffect(() => { setError(''); }, [machineId]);

    const submit = async (e) => {
        e.preventDefault();
        if (!machineId) { setError('Choose a machine to rent.'); return; }
        if (!f.start_date) { setError('Pick a start date.'); return; }
        if (clash) { setError('Some of those days are already booked — pick another start date or a shorter rental.'); return; }
        setBusy(true); setError('');
        try {
            const { data } = await api.post('/rentals/request', { ...f, machine_id: machineId, duration_count: Number(f.duration_count), origin_url: window.location.origin });
            if (window.trackConversion) window.trackConversion('rental_request', { machine: data.machine_name });
            if (data.checkout_url) { window.location.href = data.checkout_url; return; }
            setDone(data);
        } catch (err) {
            const d = err.response?.data?.detail;
            setError(typeof d === 'string' ? d : 'Please check the form — some fields are missing or invalid.');
        }
        setBusy(false);
    };

    if (done) {
        return (
            <div className="border border-emerald-200 bg-emerald-50 p-8" data-testid="rental-success-inline">
                <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                <h3 className="mt-4 font-display font-black uppercase text-3xl text-neutral-900">Request received</h3>
                <p className="mt-2 text-neutral-700">Request <b data-testid="rental-request-number">{done.request_number}</b>. We&apos;ll call or email you within one business day to confirm.</p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="rental-form">
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <L label="Machine *" span="sm:col-span-2">
                    <select value={machineId} onChange={(e) => { setMachineId(e.target.value); setError(''); }} className={inputCls} data-testid="rental-machine">
                        <option value="">Choose a machine…</option>
                        {fleet.map((m) => <option key={m.id} value={m.id}>{m.name}{m.rental_daily ? ` — ${money(m.rental_daily)}/day` : ''}</option>)}
                    </select>
                </L>
                <L label="Start date *"><RentalDatePicker value={f.start_date} onChange={(v) => setF({ ...f, start_date: v })} booked={booked} disabled={!machineId} /></L>
                <div className="grid grid-cols-2 gap-3">
                    <L label="Rent by">
                        <select value={f.duration_type} onChange={upd('duration_type')} className={inputCls} data-testid="rental-duration-type">
                            <option value="daily">Day</option><option value="weekly">Week</option>
                        </select>
                    </L>
                    <L label="How many"><input type="number" min={1} max={52} value={f.duration_count} onChange={upd('duration_count')} className={inputCls} data-testid="rental-duration-count" /></L>
                </div>
                <L label="Pickup or delivery">
                    <select value={f.delivery} onChange={upd('delivery')} className={inputCls} data-testid="rental-delivery">
                        <option value="pickup">I'll pick it up</option><option value="delivery">Deliver to my site</option>
                    </select>
                </L>
                <div className="grid grid-cols-[1fr_96px] gap-3">
                    <L label="Job city *"><input required value={f.city} onChange={upd('city')} className={inputCls} data-testid="rental-city" /></L>
                    <L label="State">
                        <select value={f.state} onChange={upd('state')} className={inputCls} data-testid="rental-state">{US_STATES.map((s) => <option key={s}>{s}</option>)}</select>
                    </L>
                </div>
                <L label="Full name *"><input required value={f.name} onChange={upd('name')} className={inputCls} data-testid="rental-name" /></L>
                <L label="Company"><input value={f.company} onChange={upd('company')} className={inputCls} data-testid="rental-company" /></L>
                <L label="Email *"><input required type="email" value={f.email} onChange={upd('email')} className={inputCls} data-testid="rental-email" /></L>
                <L label="Phone *"><input required type="tel" value={f.phone} onChange={upd('phone')} className={inputCls} data-testid="rental-phone" /></L>
                <L label="What are you cleaning? (optional)" span="sm:col-span-2"><textarea rows={3} value={f.notes} onChange={upd('notes')} className={inputCls} data-testid="rental-notes" /></L>
            </div>
            <div className="lg:col-span-5 space-y-4 lg:sticky lg:top-28 h-fit">
                {f.start_date && (
                    <p className={`text-sm ${clash ? 'text-red-600' : 'text-neutral-600'}`} data-testid="rental-date-range">
                        {clash ? 'Some of these days are already booked: ' : 'Rental period: '}
                        {parseIso(f.start_date).toLocaleDateString()} – {parseIso(endDate).toLocaleDateString()}
                    </p>
                )}
                <Estimate machine={machine} f={f} />
                {error && <p className="text-sm text-red-600 flex gap-2" data-testid="rental-error"><AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />{error}</p>}
                <button type="submit" disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-60 text-white font-bold px-6 py-4 label-caps transition-colors"
                    data-testid="rental-submit">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />} Request booking
                </button>
            </div>
        </form>
    );
};

export default RentalForm;
